import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  text?: string;
}

const svgSizes = {
  sm: { wh: 'h-4 w-4', r: 6, sw: 2 },
  md: { wh: 'h-8 w-8', r: 13, sw: 2.5 },
  lg: { wh: 'h-12 w-12', r: 20, sw: 3 },
  xl: { wh: 'h-16 w-16', r: 27, sw: 3.5 },
} as const;

export const LoadingSpinner = ({ size = 'md', className, text }: LoadingSpinnerProps) => {
  const cfg = svgSizes[size];
  const circumference = 2 * Math.PI * cfg.r;

  return (
    <div className={cn('flex flex-col items-center justify-center space-y-3', className)}>
      <svg
        className={cn('animate-[spin_1.4s_linear_infinite]', cfg.wh)}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Track ring */}
        <circle
          cx="32"
          cy="32"
          r={cfg.r}
          stroke="currentColor"
          strokeWidth={cfg.sw}
          className="text-muted-foreground/15"
        />
        {/* Animated arc with sweep */}
        <circle
          cx="32"
          cy="32"
          r={cfg.r}
          stroke="currentColor"
          strokeWidth={cfg.sw}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * 0.7}
          className="text-primary animate-[spinner-sweep_1.4s_ease-in-out_infinite]"
          style={{ transformOrigin: 'center' }}
        />
      </svg>
      {text && <p className="text-body text-muted-foreground animate-pulse font-medium">{text}</p>}
    </div>
  );
};

export const PageLoadingSpinner = () => {
  const { t } = useTranslation();

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background animate-fade-in"
      style={{ animationDelay: '150ms', animationFillMode: 'backwards' }}
    >
      <LoadingSpinner size="xl" text={t('common.loading', 'Loading...')} />
    </div>
  );
};

export const CardLoadingSpinner = () => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center p-8">
      <LoadingSpinner size="lg" text={t('common.loadingData', 'Loading data...')} />
    </div>
  );
};
